/**
 * INBOUND adapter: webshop's grid constructor contract → RfhqProductGrid.
 *
 * The adapter pattern turned around. Everywhere else we adapt a foreign data
 * shape into our model; here we implement a foreign *interface* so core can
 * keep constructing "a ProductGrid" while websitehq actually draws the cards.
 *
 * Core instantiates the grid in exactly two places — webshop/product_ui/
 * views.js render_grid_view() and render_list_view() — both with the same
 * four-key options object. That is the entire integration surface, and
 * webshop itself is never edited.
 *
 * OFF BY DEFAULT. This changes /all-products, the busiest surface on the
 * site, so it is opt-in:
 *
 *     window.rfhqCatalogTakeover = true;   // e.g. from a Website Script
 *
 * Deliberately a separate switch from `rfhqProductCardOwnership`, which
 * governs page-builder blocks: enabling those should not silently enable this.
 */

import { RfhqProductGrid } from '../grid.js';
import { WebshopSource } from './webshop_source.js';
import { cartStore } from '../cart_store.js';

const COLUMNS_BY_PREFERENCE = 3; // webshop's own grid is col-sm-4

function takeoverEnabled() {
	return typeof window !== 'undefined' && window.rfhqCatalogTakeover === true;
}

/**
 * Current listing filters, in the shape our source expects.
 *
 * Prefers core's own ProductView (exposed as window.__raftorProductView by
 * raftor_webhq's catalog_search_enter.js) so filters, item group and search
 * all agree with whatever the rest of the page thinks is applied. Falls back
 * to the URL when it is not available.
 */
function currentFilters() {
	const view = typeof window !== 'undefined' && window.__raftorProductView;
	if (view && typeof view.get_query_filters === 'function') {
		try {
			const args = view.get_query_filters();
			return {
				field_filters: args.field_filters || {},
				attribute_filters: args.attribute_filters || {},
				item_group: args.item_group || undefined,
				search: args.search || undefined,
			};
		} catch (err) {
			/* fall through to the URL */
		}
	}

	const params =
		(typeof frappe !== 'undefined' && frappe.utils && frappe.utils.get_query_params
			? frappe.utils.get_query_params()
			: {}) || {};
	const parse = (value) => {
		if (!value) return {};
		try {
			return JSON.parse(value);
		} catch (err) {
			return {};
		}
	};
	const groupEl =
		typeof document !== 'undefined' && document.querySelector('[data-item-group]');

	return {
		field_filters: parse(params.field_filters),
		attribute_filters: parse(params.attribute_filters),
		item_group: params.item_group || (groupEl ? groupEl.getAttribute('data-item-group') : undefined),
		search: params.search || undefined,
	};
}

/** Unwrap the jQuery object core passes as products_section. */
function toElement(section) {
	if (!section) return null;
	return section.jquery || section[0] ? section[0] : section;
}

export function installWebshopGridShim() {
	if (!takeoverEnabled()) return false;
	if (typeof webshop === 'undefined' || !webshop.ProductGrid) return false;
	if (webshop.ProductGrid.__rfhqShim) return true; // already installed

	// raftor_webhq's infinite_scroll.js is a second renderer AND a second
	// pagination loop. Claim its one-shot guard before frappe.ready so it
	// stands down even while it is still in the bundle — that keeps this
	// change revertible on its own, without needing the retirement commit.
	if (typeof window !== 'undefined') {
		window.__catalogExtensionsInfiniteScrollInitialized = true;
	}

	// One instance PER CONTAINER, not one globally. Core constructs a grid and
	// a list on every render — a single shared handle meant the second
	// construction tore down the first's scroll listener and cart subscription
	// while its cards were still on the page.
	const instances = new WeakMap();

	class RfhqWebshopGrid {
		/**
		 * @param {object} options webshop's contract:
		 *        { items, settings, products_section, preference }
		 */
		constructor(options) {
			const opts = options || {};
			const el = toElement(opts.products_section);
			if (!el) return;

			// webshop hardcodes this on #product-listing (the outer container —
			// products_section is #products-grid-area/#products-list-area, one
			// level *inside* it, and never carries the class itself) so its own
			// webhq_overrides.css can style the card; that CSS assumes core's
			// markup, not ours, and would double up against rfhq_product_card.css.
			// Page-builder blocks already never carry this class — strip it here
			// so websitehq's CSS is the same undisputed visual owner on the
			// catalog page too. Targeted by id, not el.parentElement, so this
			// keeps working even if core ever changes the row-wrapper nesting.
			if (typeof document !== 'undefined') {
				const listing = document.getElementById('product-listing');
				if (listing && listing.classList) listing.classList.remove('item-card-group-section');
			}

			// Core renders BOTH the grid and the list area on every pass, via
			// render_grid_view()/render_list_view(), and hides whichever is not
			// the active preference. Both calls pass the SAME opts.preference
			// (views.js's single this.preference = this.view_type — "what the
			// shopper currently wants to see", not "which of the two am I") —
			// core's *separate* ProductGrid/ProductList classes each implicitly
			// know which one they are; this one class standing in for both does
			// not, and comparing preference alone marked both slots active
			// whenever the preference was "Grid View", doubling every card.
			// #products-grid-area/#products-list-area are the stable ids core
			// itself branches on for its own view-toggle button, so they are
			// exactly what distinguishes the slot.
			const isListSlot = el.id === 'products-list-area';
			const isActive = isListSlot ? opts.preference === 'List View' : opts.preference !== 'List View';
			const $section = opts.products_section;
			if (!isActive) {
				if ($section && typeof $section.addClass === 'function') $section.addClass('hidden');
				else if (el.classList) el.classList.add('hidden');
			} else if ($section && typeof $section.removeClass === 'function') {
				$section.removeClass('hidden');
			}

			// Core empties and re-renders on every filter change, so tear down
			// this container's previous instance rather than stacking scroll
			// listeners and cart subscriptions on it.
			const previous = instances.get(el);
			if (previous) previous.destroy();

			el.innerHTML = '';

			const grid = new RfhqProductGrid({
				el,
				source: new WebshopSource({ cartStore }),
				filters: currentFilters(),
				columns: COLUMNS_BY_PREFERENCE,
				// col-sm-4 alone has no effect below 576px — cardRoot() emits no
				// xs-level class of its own. Stripping item-card-group-section
				// above also removes the !important rule that used to force 2
				// cards/row on mobile, so it's replaced here with col-6 rather
				// than left to collapse to one wide item per row.
				columnClass: 'col-6 col-sm-4',
				// Only the visible view paginates. A hidden list area scrolling
				// itself would fetch pages nobody can see.
				infiniteScroll: isActive,
				// websitehq owns the VISUALS and gives every source one
				// interface; it does not own catalog behaviour. On the catalog
				// page raftor_webhq still resolves variants, per-size stock and
				// the add-to-cart pipeline — so we draw the card and leave it
				// unclaimed for webhq to decorate. Page-builder blocks, which
				// have no such engine behind them, still claim ownership.
				claimOwnership: false,
			});

			instances.set(el, grid);
			grid.seed(opts.items, opts.settings);
			this.grid = grid;
		}
	}

	RfhqWebshopGrid.__rfhqShim = true;
	// Respect the flag product_card_contract.js sets, so its own make() patch
	// does not also try to wrap what is no longer core's class.
	RfhqWebshopGrid.__raftorCardPatch = true;

	webshop.ProductGrid = RfhqWebshopGrid;
	if (webshop.ProductList) webshop.ProductList = RfhqWebshopGrid;

	return true;
}
